import React, { useRef, useState } from 'react';

interface OTPInputProps {
  length: number;
  onChange: (otp: string) => void;
  autoFocus?: boolean;
  isNumeric?: boolean;
  errorMessage?: string | JSX.Element | null;
}

const OTPInput: React.FC<OTPInputProps> = ({ length, onChange, autoFocus = true, isNumeric = true, errorMessage }) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleChange = (value: string, index: number) => {
    if (isNumeric && !/^\d*$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // keep only last character
    setOtp(newOtp);
    onChange(newOtp.join(''));

    if (value && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const key = e.key;

    if (key === 'Backspace') {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
        onChange(newOtp.join(''));
      } else if (index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').slice(0, length);
    const chars = isNumeric ? paste.replace(/\D/g, '') : paste.split('');
    const newOtp = [...otp];

    for (let i = 0; i < length; i++) {
      newOtp[i] = chars[i] || '';
    }

    setOtp(newOtp);
    onChange(newOtp.join(''));

    const nextEmptyIndex = newOtp.findIndex((c) => !c);
    if (nextEmptyIndex !== -1) {
      inputRefs.current[nextEmptyIndex]?.focus();
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '3rem',
    height: '3rem',
    textAlign: 'center',
    fontSize: '1.5rem',
    border: `2px solid ${errorMessage ? '#e53935' : '#ccc'}`,
    borderRadius: '0.5rem',
    outline: 'none',
    boxSizing: 'border-box',
  };

  return (
    <div>
      <div role="group" aria-label="OTP Input" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
        {otp.map((digit, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type={isNumeric ? 'tel' : 'text'}
            inputMode={isNumeric ? 'numeric' : 'text'}
            pattern={isNumeric ? '[0-9]*' : undefined}
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(e.target.value, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
            aria-label={`Digit ${index + 1}`}
            autoFocus={autoFocus && index === 0}
            style={inputStyle}
          />
        ))}
      </div>

      {errorMessage && (
        <div
          role="alert"
          style={{
            color: '#e53935',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '0.5rem',
            justifyContent: 'center',
          }}
        >
          {typeof errorMessage === 'string' ? <span>{errorMessage}</span> : errorMessage}
        </div>
      )}
    </div>
  );
};

export default OTPInput;
